'use client';

import { Spinner } from '@heroui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Pagination,
} from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import { useStudioSystemStorageList } from '@/hooks/use-studio-system-storage-list';
import { cn } from '@/utils';

const PAGE_SIZE = 24;

const REALM = process.env.NEXT_PUBLIC_AUTH_SERVER_REALM ?? 'mtb';
const IMAGE_REGEX = `${REALM}/assets/.*/.*.*.(jpg|gif|jpeg|svg|avif|png|webp)$`;

export type StorageGalleryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (imageUrls: string[]) => void;
  /** URLs already selected by the parent (shown as selected in the grid). */
  selectedUrls?: string[];
  /** Allow selecting multiple images. Default false (single select). */
  multiple?: boolean;
  disabled?: boolean;
  storage?: string;
};

export const StorageGalleryModal: FC<StorageGalleryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedUrls = [],
  multiple = false,
  disabled = false,
  storage,
}) => {
  const [page, setPage] = useState(1);
  const [localSelected, setLocalSelected] = useState<Set<string>>(new Set(selectedUrls));

  useEffect(() => {
    setLocalSelected(new Set(selectedUrls));
  }, [selectedUrls]);

  const handleClick = useCallback(
    (url: string) => {
      if (disabled) return;

      if (multiple) {
        setLocalSelected(prev => {
          const wasSelected = prev.has(url);
          const next = new Set(prev);
          if (wasSelected) {
            next.delete(url);
          } else {
            next.add(url);
          }
          return next;
        });
      } else {
        setLocalSelected(new Set([url]));
      }
    },
    [disabled, multiple],
  );

  const handleSave = useCallback(() => {
    onSave(Array.from(localSelected));
    onClose();
  }, [localSelected, onClose, onSave]);

  const { data, isLoading } = useStudioSystemStorageList({
    from: (page - 1) * PAGE_SIZE,
    size: PAGE_SIZE,
    storage,
    regex: IMAGE_REGEX,
    enabled: isOpen,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">Select from gallery</ModalHeader>
        <ModalBody className="!min-h-[600px]">
          {isLoading ? (
            <div className="flex min-h-[700px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-zinc-500">No images in gallery.</p>
          ) : (
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6">
              {items.map((item, index) => {
                const url = item.publicUrl ?? item.url ?? '';
                const isSelected = localSelected.has(url);
                return (
                  <button
                    type="button"
                    key={item.id ?? url ?? index}
                    disabled={disabled}
                    className={cn(
                      'group relative aspect-square overflow-hidden rounded-lg border-2 bg-zinc-100 p-0',
                      isSelected ? 'border-primary ring-primary/30 ring-2' : 'border-zinc-200',
                      !disabled &&
                        'hover:border-primary hover:ring-primary/20 cursor-pointer hover:ring-2',
                    )}
                    onClick={() => handleClick(url)}
                    aria-label={isSelected ? 'Deselect image' : 'Select image'}
                  >
                    <RenderableImage
                      src={url}
                      alt={item.fileName ?? 'Gallery image'}
                      className="h-full w-full object-cover"
                    />
                    {isSelected && (
                      <div className="bg-primary/10 pointer-events-none absolute inset-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination total={totalPages} page={page} onChange={setPage} showControls />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>
            Close
          </Button>
          <Button color="primary" onPress={handleSave} disabled={disabled}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
